
import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/hooks/useTheme";
import { LanguageProvider } from "@/hooks/useLanguage";
import ScrollToTop from "./components/ScrollToTop";
import { HackerBackground } from "@/components/layout/HackerBackground";

// Eager load - critical pages (Dashboard, Index, NotFound)
import Dashboard from "./pages/Dashboard";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load - heavy pages (code-splitting for bundle optimization)
const Leaks = React.lazy(() => import("./pages/Leaks"));
const LeakDetails = React.lazy(() => import("./pages/LeakDetails"));
const CVE = React.lazy(() => import("./pages/CVE"));
const CVEDetail = React.lazy(() => import("./pages/CVEDetail"));
const Ransomware = React.lazy(() => import("./pages/Ransomware"));
const EOL = React.lazy(() => import("./pages/EOL"));
const Releases = React.lazy(() => import("./pages/Releases"));
const Scan = React.lazy(() => import("./pages/Scan"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/30 border-t-primary"></div>
      <p className="text-muted-foreground text-sm animate-pulse">Loading...</p>
    </div>
  </div>
);

// Create a new query client with improved caching strategies
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <HackerBackground />
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Make Dashboard the root page */}
                <Route path="/" element={<Dashboard />} />
                <Route path="/news" element={<Index />} />
                <Route path="/leaks" element={<Leaks />} />
                <Route path="/leaks/:domain" element={<LeakDetails />} />
                <Route path="/CVE" element={<CVE />} />
                <Route path="/CVE/:cveId" element={<CVEDetail />} />
                <Route path="/ransomware/:countryCode?" element={<Ransomware />} />
                <Route path="/ransomware/group/:groupName" element={<Ransomware />} />
                <Route path="/eol" element={<EOL />} />
                <Route path="/eol/:product" element={<EOL />} />
                <Route path="/scan" element={<Scan />} />
                <Route path="/releases" element={<Releases />} />
                {/* Redirect /dashboard to root for backward compatibility */}
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
